import { useState, useCallback } from "react";
import type { KeyboardEvent, Dispatch, SetStateAction } from "react";
import { tryAddEmail } from "../components/ReferUserDialog.utils";

interface UseReferralEmailsReturn {
  emails: string[];
  inputValue: string;
  isSending: boolean;
  setEmails: Dispatch<SetStateAction<string[]>>;
  setInputValue: Dispatch<SetStateAction<string>>;
  setIsSending: Dispatch<SetStateAction<boolean>>;
  addEmail: (emailInput: string) => void;
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}

export function useReferralEmails(): UseReferralEmailsReturn {
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);

  const addEmail = useCallback(
    (emailInput: string): void => {
      tryAddEmail(
        emailInput,
        emails,
        (e) => setEmails(e),
        (v) => setInputValue(v)
      );
    },
    [emails]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (["Enter", ","].includes(e.key)) {
        e.preventDefault();
        addEmail(inputValue);
      } else if (e.key === "Backspace" && inputValue === "" && emails.length > 0) {
        e.preventDefault();
        // Use functional update to avoid stale closure on emails
        setEmails((prev) => prev.slice(0, -1));
      }
    },
    [addEmail, inputValue, emails.length]
  );

  return {
    emails,
    inputValue,
    isSending,
    setEmails,
    setInputValue,
    setIsSending,
    addEmail,
    handleKeyDown,
  };
}
